import { FC } from "react";
import { useTranslation } from "react-i18next";

import { AccountStatement } from "interface/accounting";
import { formatAmount, formatDate } from "utils/formatters";

interface Props {
	items: AccountStatement[];
	loading: boolean;
	periods: number[];
}

const AccountStatementMobileList: FC<Props> = ({ items, loading, periods }) => {
	const { t } = useTranslation();
	const account = items[0];
	const minimumPeriod = periods.length > 0 ? Math.min(...periods) : "";
	const maximumPeriod = periods.length > 0 ? Math.max(...periods) : "";

	if (!loading && items.length === 0) {
		return <div>{t("NoRows")}</div>;
	}

	const balanceRow = (
		label: string,
		balance: number,
		period: number | string,
	) => (
		<div
			style={{
				alignItems: "center",
				gap: 16,
				padding: "12px 16px",
				fontWeight: 700,
				backgroundColor: "#fff",
			}}
		>
			<div style={{ marginBottom: "10px" }}>{label}</div>
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<span>
					{account?.accountingYear ?? ""} / {period}
				</span>
				<span>{formatAmount(balance)}</span>
			</div>
		</div>
	);

	return (
		<div
			style={{
				height: "calc(100vh - 450px)",
				minHeight: 280,
				width: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{balanceRow(
				t("PreviousBalance"),
				account?.balanceBeforePeriod ?? 0,
				minimumPeriod,
			)}

			<div
				className="sales-note-list-mobile"
				style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
			>
				{items.map((item, index) => (
					<div
						key={`${item.voucherNumber}-${index}`}
						className="sales-note-list-card"
					>
						<div className="sales-note-list-card-header">
							<div className="sales-note-list-card-note">
								{item.voucherNumber}
							</div>
							<div className="sales-note-list-card-vessel">
								{item.voucherType}
							</div>
							<div className="sales-note-list-card-date">
								{formatDate(item.voucherDate)}
							</div>
						</div>
						<div className="sales-note-list-card-body">
							<div>
								<span>{t("InvoiceNumber")}</span>
								<strong>{item.invoiceNumber?.trim() || "-"}</strong>
							</div>
							<div>
								<span>{t("Amount")}</span>
								<strong>{formatAmount(item.amount)}</strong>
							</div>
							<div>
								<span>{t("RemainingAmount")}</span>
								<strong>{formatAmount(item.remainingAmount)}</strong>
							</div>
							<div>
								<span>{t("DueDate")}</span>
								<strong>{formatDate(item.dueDate)}</strong>
							</div>
							<div>
								<span>{t("PaidDate")}</span>
								<strong>{formatDate(item.paidDate)}</strong>
							</div>
						</div>
					</div>
				))}
				{loading && <div>{t("Loading")}</div>}
			</div>

			{balanceRow(
				t("NewBalance"),
				account?.balanceAfterPeriod ?? 0,
				maximumPeriod,
			)}
		</div>
	);
};

export default AccountStatementMobileList;
